<script setup lang="ts">
import { MatchThePairs } from '@ilteducation/games';
import { ref, computed, watch } from 'vue';
import type { Language } from '@ilteducation/content-ref-data-types/models/data-types';
import config from '@/config/ConfigProvider';
import useLanguagesStore from '@/store/languages';
import { useVideos } from '@ilteducation/videos';
import { useCategories } from '@ilteducation/content-ref-data';
import useKeywordsStore, { KeywordWithTranslations } from '@/store/keywords';
import useAudioStore from '@/store/audio';
import useGuiStore from '@/store/gui';
import { useContentHref } from '@/composables/use-content-href';
import { useRoute, useRouter } from 'vue-router';
import { useLocale } from '@/plugins/translation';
import type { Video } from '@ilteducation/videos';

const { audioResources } = useAudioStore();
const route = useRoute();
const router = useRouter();
const { getVideoHref } = useContentHref();
const { getThemeName } = useGuiStore();
const videoStore = useVideos();
const { categories } = useCategories();
const { t, l } = useLocale();

const languagesStore = useLanguagesStore();
const keywordsStore = useKeywordsStore();
const keywords = ref<KeywordWithTranslations[]>([]);
const keywordsLoaded = ref(false);

const videoId = ref(route.params.videoId?.toString());
let video = ref<Video>();

watch(
  videoId,
  async (id) => {
    if (id) {
      video.value = await videoStore.getVideoById(id);
      keywords.value = await keywordsStore.getKeywordTranslations(video.value.keywords);
      keywordsLoaded.value = true;
    }
  },
  { immediate: true },
);

const matchThePairsKeywords = computed(() =>
  keywords.value.map((keyword) => {
    const transformedTranslations: { [lang: string]: { title: string; description: string; descriptionLong: string } } =
      {};
    keyword.supportedLanguages.forEach((lang) => {
      transformedTranslations[lang] = {
        title: keyword.translations?.title?.[lang]?.translation,
        description: keyword.translations?.description?.[lang]?.translation || '',
        descriptionLong: keyword.translations?.descriptionLong?.[lang]?.translation || '',
      };
    });

    return {
      ...keyword,
      translations: transformedTranslations,
    };
  }),
);
const translateLanguageName = ({ language, languageCode }: Language): string => {
  // get language name in current UI language
  const trans = t(`languages.${languageCode}`) as string;
  if (trans === `languages.${languageCode}`) {
    return language;
  }
  return trans;
};

const languages = computed<Language[]>(() =>
  languagesStore.languages.map((lang) => ({
    ...lang,
    language: translateLanguageName(lang),
  })),
);

const subjectSlug = computed(() => route.query.subject?.toString());
const categoryId = computed(() => categories.value.find((c) => c.slug === subjectSlug.value)?.id);

const gameTheme = computed(() => {
  const subjectTheme = getThemeName(subjectSlug.value ?? '').replace(/Dark$/, '');
  return subjectTheme === 'light' ? 'baseDark' : `${subjectTheme}Dark`;
});

const contentRegistryUrl: string = config.value('contentRegistryUrl');
const gameComponentRef = ref<typeof MatchThePairs | null>(null);

const goBack = () => {
  if (window.history.state?.back) {
    router.back();
    return;
  }
  if (!video.value) {
    return;
  }
  const url = getVideoHref(video.value, categoryId.value);
  if (!url) {
    return;
  }
  router.push(url);
};
</script>

<template>
  <v-container fluid class="match-the-pairs-game pa-0">
    <v-card class="w-100 h-100 position-relative overflow-y-auto overflow-x-hidden" rounded="0">
      <div v-if="!keywordsLoaded" class="d-flex justify-center align-center pa-16">
        <v-progress-circular indeterminate color="primary" size="64" :aria-label="t(l.label_loading)" />
      </div>
      <MatchThePairs
        v-else
        ref="gameComponentRef"
        :t="t"
        :keywords="matchThePairsKeywords"
        :languages="languages"
        :audio-resources="audioResources"
        :content-registry-url="contentRegistryUrl"
        :theme="gameTheme"
        :default-language="languagesStore.getMarketLanguage() === 'nb' ? 'no-nb' : languagesStore.getMarketLanguage()"
        @close="goBack"
      />
    </v-card>
  </v-container>
</template>

<style scoped lang="scss">
.match-the-pairs-game {
  height: calc(100dvh - var(--main-top-padding) - var(--main-bottom-padding));
  max-height: calc(100dvh - var(--main-top-padding) - var(--main-bottom-padding));
}
</style>
